import {FC, useState} from 'react'
import {Link} from 'react-router-dom'
import {PrintFlowOutcome} from '../printFlow/outcomeModel'
import {formatTimestamp} from '../time'

type Props = {outcome: PrintFlowOutcome}

const roleLabel = (role: string) => {
  switch (role) {
    case 'assigned':
      return 'Assigned'
    case 'actual_printer':
      return 'Actual printer'
    default:
      return 'Cache target'
  }
}

export const PrinterDeliveryMatrix: FC<Props> = ({outcome}) => {
  const [showJobs, setShowJobs] = useState(false)
  const totalJobs = outcome.devices.reduce((sum, d) => sum + d.jobCount, 0)

  return (
    <div className='card card-flush border border-gray-200 mb-6'>
      <div className='card-header border-0 pt-5 pb-0'>
        <div className='card-title flex-column align-items-start'>
          <span className='fw-bold text-gray-800'>Printer delivery</span>
          <span className='text-muted fs-7 fw-semibold mt-1'>
            Grouped by device — not every code×device retry
          </span>
        </div>
      </div>
      <div className='card-body pt-5'>
        {outcome.devices.length === 0 ? (
          <div className='text-gray-700 fs-6'>No printers were targeted for prefetch or print.</div>
        ) : (
          <div className='d-flex flex-column gap-5'>
            {outcome.devices.map((device) => (
              <div key={device.deviceId} className='border border-gray-200 rounded p-4'>
                <div className='d-flex align-items-start justify-content-between gap-3 mb-3'>
                  <div>
                    <div className='fw-bold text-gray-800 fs-5'>{device.deviceId}</div>
                    <div className='text-gray-700 fs-6'>{roleLabel(device.role)}</div>
                  </div>
                  <Link
                    to={`/apps/printer-management/printers/${encodeURIComponent(device.deviceId)}`}
                    className='btn btn-sm btn-light-primary'
                  >
                    Open
                  </Link>
                </div>

                <div className='mb-2'>
                  <span className='fw-bold text-gray-800 fs-7'>Status</span>
                  <div className='text-gray-700 fs-6'>{device.connectionLabel}</div>
                </div>
                <div className='mb-2'>
                  <span className='fw-bold text-gray-800 fs-7'>Assets sent</span>
                  <div className='text-gray-700 fs-6'>{device.assetsSent}</div>
                </div>
                <div className='mb-2'>
                  <span className='fw-bold text-gray-800 fs-7'>Latest command</span>
                  <div className='text-gray-700 fs-6'>
                    {device.latestCommandAt ? formatTimestamp(device.latestCommandAt) : '—'}
                  </div>
                </div>
                <div className='mb-2'>
                  <span className='fw-bold text-gray-800 fs-7'>Cache confirmations</span>
                  <div className='text-gray-700 fs-6'>
                    {device.cacheConfirmed} of {device.assetsSent || device.jobCount}
                  </div>
                </div>
                <div className='mb-2'>
                  <span className='fw-bold text-gray-800 fs-7'>Heartbeat</span>
                  <div className='text-gray-700 fs-6'>{device.heartbeatLabel}</div>
                </div>
                <div className='mb-2'>
                  <span className='fw-bold text-gray-800 fs-7'>Physical print</span>
                  <div className='text-gray-700 fs-6'>{device.physicalPrint}</div>
                </div>
                {device.issue && (
                  <div className='mt-3 text-gray-700 fs-6'>
                    <span className='fw-bold'>Issue:</span> {device.issue}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {totalJobs > outcome.devices.length && (
          <div className='mt-5'>
            <button
              type='button'
              className='btn btn-sm btn-light'
              onClick={() => setShowJobs((v) => !v)}
            >
              {showJobs ? 'Hide' : 'View'} {totalJobs} underlying prefetch jobs
            </button>
            {showJobs && (
              <div className='text-gray-700 fs-6 mt-3'>
                Prefetch fan-out produced {totalJobs} job documents across{' '}
                {outcome.devices.length} device(s). Use the JSON tab for full job payloads, trace
                IDs, and retries.
              </div>
            )}
          </div>
        )}

        <div className='table-responsive mt-6'>
          <table className='table align-middle gs-0 gy-3 mb-0'>
            <thead>
              <tr className='text-muted fw-bold fs-8 text-uppercase'>
                <th>Device</th>
                <th>Route role</th>
                <th>Connection</th>
                <th>Prefetch</th>
                <th>Physical print</th>
              </tr>
            </thead>
            <tbody>
              {outcome.devices.map((device) => (
                <tr key={`row-${device.deviceId}`}>
                  <td className='fw-semibold text-gray-800'>{device.deviceId}</td>
                  <td className='text-gray-700'>{roleLabel(device.role)}</td>
                  <td className='text-gray-700'>{device.connectionLabel}</td>
                  <td className='text-gray-700'>
                    {device.cacheConfirmed}/{device.assetsSent || device.jobCount} confirmed
                  </td>
                  <td className='text-gray-700'>{device.physicalPrint}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
